import { AppleIapInfoText } from '@/components/apple-iap-info-text';
import {
  GET_IN_TOUCH_URL,
  getComparePlansUrl,
} from '@/components/constants/billing-urls';
import { pricingPlansModalMessages } from '@/features/settings/billing/components/pricing-plans-modal/pricing-plans-modal.messages';
import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import { cn } from '@/lib/cn';
import { Link } from '@heidi/design-system/link';
import { TypographyCaption } from '@heidi/design-system/typography';
import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { ExternalLink } from 'lucide-react';
import { useCallback, useRef, useState, type CSSProperties } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AvailableRolesSection } from './available-roles-section';
import { ClinicianMaxPricingPlanCard } from './clinician-max-pricing-plan-card';
import { PlanSeatTabs } from './plan-seat-tabs';
import { PricingPlanCard } from './pricing-plan-card';
import { usePricingPlansModalFeatures } from './pricing-plans-modal.features';
import { usePricingPlanColumns } from './use-pricing-plan-columns';
import { usePricingPlansModalCtas } from './usePricingPlansModalCtas';
import type { UsePricingPlansModalStateReturn } from './usePricingPlansModalState';

export function PricingPlanCards({
  modalState,
}: {
  modalState: UsePricingPlansModalStateReturn;
}) {
  const { locale, formatMessage } = useIntl();

  const {
    clinicianProPlanCtaLabel,
    clinicianProPlusPlanCtaLabel,
    practiceMaxPlanCtaLabel,
    onClinicianProPlanCtaClick,
    onClinicianProPlusPlanCtaClick,
    onPracticeMaxPlanCtaClick,
    onTalkToSalesClick,
    isAppleCompliant,
  } = usePricingPlansModalCtas(modalState);

  const {
    isPending,
    currentPlan,
    isPracticeMaxSeat,
    isTrialingClinician,
    isClinicianStandardPlan,
    isPracticeStandardPlan,
    legacyTier,
  } = modalState;

  // A Clinician trial still reports the trial tier. Copy follows the plan they
  // pay for, which is the legacy tier until the trial converts.
  const copyTier = isTrialingClinician
    ? (legacyTier ?? currentPlan)
    : (currentPlan ?? legacyTier);

  const {
    showClinician,
    showClinicianMax,
    showPractice,
    showSeatToggle,
    visibleCardCount,
    selectedPracticeSeat,
    setSelectedPracticeSeat,
    isPracticeMaxSelected,
    isClinicianHighlighted,
    isClinicianMaxHighlighted,
    isPracticeHighlighted,
  } = usePricingPlanColumns(modalState);

  const {
    clinicianFeatures,
    clinicianFeaturesTitle,
    practiceFeatures,
    practiceFeaturesTitle,
    practiceMaxFeatures,
    practiceMaxFeaturesTitle,
  } = usePricingPlansModalFeatures(copyTier);
  const {
    hasTeam,
    isTeamStripeManaged,
    isNonAdminTeamMember,
    isLoadingTeamDetails,
    isTeamDataReady,
  } = useCombinedQueriesTeamGeneralInfo();

  // Role and Stripe management both read as "blocked" until team details settle
  // (`isTeamAdmin` undefined, `is_stripe_managed` defaults false). Hold the
  // upgrade closed for that window instead of flashing the wrong tooltip.
  // Omitted flags (older mocks) are not a hold.
  const teamRoleUnresolved = !!isLoadingTeamDetails || isTeamDataReady === false;
  const settledNonAdmin = !!isNonAdminTeamMember && !teamRoleUnresolved;
  const shouldDisableUpgradeForNonStripeManagedTeam =
    hasTeam && !isTeamStripeManaged && !teamRoleUnresolved;
  // A trial on the current plan still checks out ("Upgrade now"), so non-admins
  // are blocked there too. Non-Stripe teams keep the older "not this tier" check.
  const clinicianCtaIsUpgrade = !isClinicianStandardPlan || isTrialingClinician;
  const practiceCtaIsUpgrade = isPracticeMaxSelected
    ? !isPracticeMaxSeat || isTrialingClinician
    : !isPracticeStandardPlan || isTrialingClinician;
  const upgradeDisabledTooltip = settledNonAdmin
    ? formatMessage(pricingPlansModalMessages.contactTeamAdminToUpgrade)
    : shouldDisableUpgradeForNonStripeManagedTeam
      ? formatMessage(pricingPlansModalMessages.contactSalesToUpgrade)
      : undefined;

  const pricingPlansUrl = getComparePlansUrl(locale);

  const planRowRef = useRef<HTMLDivElement>(null);
  const [pinnedRowHeight, setPinnedRowHeight] = useState<string>();

  /**
   * Opening "Available roles" has to scroll the Practice card's feature
   * section, not grow the dialog, so the row is frozen at the height it
   * already had. Radix reports
   * the change before the panel renders, which is what makes the collapsed
   * height still readable here.
   *
   * The pin is never released: it IS the collapsed height, so keeping it holds
   * the row still through the open and close animations alike. Releasing it on
   * close let the row track the panel as it shrank.
   *
   * ponytail: a pixel snapshot, so a viewport resize afterwards leaves it
   * stale. Upgrade path is a ResizeObserver on the row if that ever bites.
   */
  const handleRolesExpand = useCallback(() => {
    const height = planRowRef.current?.offsetHeight;
    if (height) {
      setPinnedRowHeight(`${height}px`);
    }
  }, []);

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      {/*
        An unset `--plan-row-height` makes the `height` declaration invalid at
        computed-value time, so the row falls back to `auto` and sizes to its
        cards. Only applied from `md`: stacked, the cards scroll with the body.
      */}
      <div
        ref={planRowRef}
        data-testid="pricing-plan-row"
        style={{ '--plan-row-height': pinnedRowHeight } as CSSProperties}
        className={cn(
          'grid w-full min-w-0 grid-cols-1 gap-4 md:h-[var(--plan-row-height)] md:items-stretch',
          visibleCardCount === 3 && 'md:grid-cols-3',
          visibleCardCount === 2 && 'md:grid-cols-2',
          // A lone column keeps a card's width rather than spanning the dialog.
          visibleCardCount === 1 && 'md:w-1/2 md:grid-cols-1 md:self-center'
        )}
      >
        {showClinician && (
          <PricingPlanCard
            tier={SubscriptionTier.CLINICIAN_PRO}
            isHighlighted={isClinicianHighlighted}
            planSubtitle={
              <FormattedMessage {...pricingPlansModalMessages.planSubtitleClinician} />
            }
            features={clinicianFeatures}
            featuresSectionTitle={<FormattedMessage {...clinicianFeaturesTitle} />}
            ctaText={clinicianProPlanCtaLabel}
            onCtaClick={isAppleCompliant ? undefined : onClinicianProPlanCtaClick}
            isCtaLoading={isPending}
            isCtaDisabled={
              ((teamRoleUnresolved || settledNonAdmin) && clinicianCtaIsUpgrade) ||
              (currentPlan !== SubscriptionTier.CLINICIAN_PRO &&
                shouldDisableUpgradeForNonStripeManagedTeam)
            }
            ctaDisabledTooltip={upgradeDisabledTooltip}
          />
        )}

        {showClinicianMax && (
          <ClinicianMaxPricingPlanCard
            isNonStripeManagedTeam={shouldDisableUpgradeForNonStripeManagedTeam}
            isNonAdminTeamMember={settledNonAdmin}
            isTeamRoleUnresolved={teamRoleUnresolved}
            isHighlighted={isClinicianMaxHighlighted}
          />
        )}

        {showPractice && (
          <PricingPlanCard
            tier={SubscriptionTier.CLINICIAN_PRO_PLUS}
            seatType={
              isPracticeMaxSelected ? BillingSeatType.MAX : BillingSeatType.STANDARD
            }
            seatToggle={
              showSeatToggle ? (
                <PlanSeatTabs
                  value={selectedPracticeSeat}
                  onValueChange={setSelectedPracticeSeat}
                  isHighlighted={isPracticeHighlighted}
                />
              ) : undefined
            }
            isHighlighted={isPracticeHighlighted}
            planSubtitle={
              <FormattedMessage
                {...(isPracticeMaxSelected
                  ? pricingPlansModalMessages.planSubtitlePracticeMax
                  : pricingPlansModalMessages.planSubtitlePractice)}
              />
            }
            features={isPracticeMaxSelected ? practiceMaxFeatures : practiceFeatures}
            featuresSectionTitle={
              <FormattedMessage
                {...(isPracticeMaxSelected
                  ? practiceMaxFeaturesTitle
                  : practiceFeaturesTitle)}
              />
            }
            ctaText={
              isPracticeMaxSelected
                ? practiceMaxPlanCtaLabel
                : clinicianProPlusPlanCtaLabel
            }
            onCtaClick={
              isAppleCompliant
                ? undefined
                : isPracticeMaxSelected
                  ? onPracticeMaxPlanCtaClick
                  : onClinicianProPlusPlanCtaClick
            }
            isCtaLoading={isPending}
            isCtaDisabled={
              ((teamRoleUnresolved || settledNonAdmin) && practiceCtaIsUpgrade) ||
              ((isPracticeMaxSelected
                ? !isPracticeMaxSeat
                : currentPlan !== SubscriptionTier.CLINICIAN_PRO_PLUS) &&
                shouldDisableUpgradeForNonStripeManagedTeam)
            }
            ctaDisabledTooltip={upgradeDisabledTooltip}
            footer={<AvailableRolesSection onExpand={handleRolesExpand} />}
          />
        )}
      </div>

      {isAppleCompliant ? (
        <div className="flex w-full justify-center py-4">
          <AppleIapInfoText className="text-center text-text-secondary" />
        </div>
      ) : (
        <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
          <div className="flex items-center gap-1">
            <TypographyCaption className="text-text-secondary">
              <FormattedMessage {...pricingPlansModalMessages.salesTaxNote} />
            </TypographyCaption>
            <Link
              href={pricingPlansUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="inline"
              size="xs"
            >
              <FormattedMessage {...pricingPlansModalMessages.learnMore} />
            </Link>
          </div>
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage
              {...pricingPlansModalMessages.talkToSales}
              values={{
                link: (
                  <Link
                    href={GET_IN_TOUCH_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="inline"
                    size="inherit"
                    onClick={onTalkToSalesClick}
                  >
                    <FormattedMessage {...pricingPlansModalMessages.talkToSalesCta} />
                    <ExternalLink className="size-3" aria-hidden />
                  </Link>
                ),
              }}
            />
          </TypographyCaption>
        </div>
      )}
    </div>
  );
}
